// 聊天转引导模式服务 - 将聊天记录转换为引导模式数据结构
import chatAnalysisService from './chatAnalysisService.js'
import { RealAIService } from './realAiService.js'
import settingsService from './settingsService.js'

class ChatToGuidedService {
  /**
   * 将聊天记录转换为引导模式数据
   */
  async convertToGuidedData(character, messages) {
    try {
      // 1. 分析聊天记录
      const analysis = await chatAnalysisService.analyzeChatHistory(messages)
      
      // 2. 生成故事大纲
      const outline = await this.generateStoryOutline(character, messages, analysis)
      
      // 3. 提取关键对话作为选项
      const keyConversations = chatAnalysisService.extractKeyConversations(messages, analysis)
      
      // 4. 生成场景描述和旁白
      const scenes = await this.generateScenes(messages, analysis, keyConversations)
      
      // 5. 构建引导模式数据结构
      const guidedData = {
        userDescription: `与${character.name}的聊天记录`,
        genre: '情感互动',
        artStyle: '现代',
        topic: `${character.name}的故事`,
        targetAudience: '所有用户',
        outline: outline,
        characters: [{
          name: character.name,
          description: character.description,
          personality: character.personality
        }],
        scenes: scenes,
        storyboard: {
          chapters: [{
            id: 1,
            scenes: scenes.map((scene, index) => ({
              id: `M${index + 1}`,
              title: scene.title,
              content: scene.content,
              dialogues: scene.dialogues || [],
              choices: scene.choices || [],
              hasChoices: (scene.choices || []).length > 0,
              background: scene.background || '',
              mood: scene.mood || '普通'
            }))
          }]
        }
      }
      
      return guidedData
    } catch (error) {
      console.error('❌ 转换失败:', error)
      throw error
    }
  }

  /**
   * 生成故事大纲
   */
  async generateStoryOutline(character, messages, analysis) {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig.apiKey || !languageConfig.apiUrl) {
        return this.generateSimpleOutline(character, messages)
      }

      const conversationSummary = messages
        .slice(0, 20) // 取前20条消息
        .map(msg => `${msg.sender === 'user' ? '用户' : character.name}：${msg.content}`)
        .join('\n')

      const prompt = `基于以下与${character.name}的对话记录，生成一个互动故事大纲：

${conversationSummary}

请生成：
1. 故事标题（吸引人）
2. 故事简介（100字内）
3. 主要情节（3-5个关键场景）

请以JSON格式返回：
{
  "title": "故事标题",
  "summary": "故事简介",
  "mainPlot": "主要情节描述",
  "keyScenes": [
    "场景1描述",
    "场景2描述"
  ]
}`

      const response = await RealAIService.callLanguageModel(prompt)
      
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          return JSON.parse(jsonMatch[0])
        }
      } catch (e) {
        console.warn('解析大纲JSON失败，使用降级方案')
      }
      
      return this.generateSimpleOutline(character, messages)
    } catch (error) {
      console.error('❌ 生成故事大纲失败:', error)
      return this.generateSimpleOutline(character, messages)
    }
  }

  /**
   * 生成简单大纲（降级方案）
   */
  generateSimpleOutline(character, messages) {
    return {
      title: `与${character.name}的故事`,
      summary: `基于与${character.name}的${messages.length}条对话记录创建的互动故事。`,
      mainPlot: `记录了你与${character.name}之间的对话和互动。`,
      keyScenes: messages.slice(0, 5).map((msg, i) => `场景${i + 1}：${msg.content.substring(0, 50)}...`)
    }
  }

  /**
   * 生成场景
   */
  async generateScenes(messages, analysis, keyConversations) {
    const scenes = []
    
    // 将消息分组为场景（每5-10条消息一个场景）
    const messagesPerScene = 8
    for (let i = 0; i < messages.length; i += messagesPerScene) {
      const sceneMessages = messages.slice(i, i + messagesPerScene)
      
      // 构建场景内容
      const sceneContent = sceneMessages
        .map(msg => `${msg.sender === 'user' ? '你' : '角色'}：${msg.content}`)
        .join('\n')
      
      // 检查是否是关键节点
      const isKeyScene = sceneMessages.some(msg => 
        analysis.emotionalHighs.some(h => h.messageId === msg.id) ||
        analysis.turningPoints.some(t => t.messageId === msg.id)
      )
      
      // 提取用户消息作为选项
      const userMessages = sceneMessages.filter(msg => msg.sender === 'user')
      const choices = userMessages.length > 0 && isKeyScene
        ? userMessages.slice(0, 2).map((msg, idx) => ({
            text: msg.content.substring(0, 30),
            targetNodeId: `M${Math.floor(i / messagesPerScene) + 2}`,
            id: `choice-${i}-${idx}`
          }))
        : []
      
      scenes.push({
        title: `场景 ${Math.floor(i / messagesPerScene) + 1}`,
        content: sceneContent,
        dialogues: sceneMessages.map(msg => ({
          character: msg.sender === 'user' ? '你' : '角色',
          content: msg.content
        })),
        choices: choices,
        background: '基于聊天记录生成的场景',
        mood: isKeyScene ? '重要' : '普通'
      })
    }
    
    return scenes
  }
}

// 导出单例
const chatToGuidedService = new ChatToGuidedService()

export default chatToGuidedService

